/**
 * Rewrite Host-tenant: barra pública ↔ ruta interna `/market/:slug/...`.
 * Sin 302. Home y deep links (`/catalogo`, `/p/...`, etc.).
 *
 * Panel / auth (`/app`, `/login`, …) son pass-through: misma forma interna y pública.
 * input/output deben ser inversas — si no, Transitioner.commitLocation entra en loop.
 *
 * @see https://tanstack.com/router/latest/docs/framework/react/guide/url-rewrites
 */
import type { LocationRewrite } from "@tanstack/react-router";
import { peekTenantHostSlug } from "@/lib/tenant-host-slug";

/** Superficies de plataforma / panel: no viven bajo `/market/:slug`. */
const PASS_THROUGH =
  /^\/(api|_server|_redirect|assets|favicon\.ico|institucional|admin|app|builder|owner|platform|login|register|forgot-password|reset-password|acceso|auth)(\/|$)/i;

function marketPrefix(slug: string): string {
  return `/market/${slug}`;
}

function withPathname(url: URL, pathname: string): URL {
  if (url.pathname === pathname) return url;
  const next = new URL(url.href);
  next.pathname = pathname;
  return next;
}

/**
 * Browser / history → ruta interna del router.
 * Canoniza `/market/:slug/app/...` → `/app/...` para no pelear con PASS_THROUGH.
 */
export function toInternalPathname(pathname: string, slug: string): string {
  const p = pathname || "/";
  const prefix = marketPrefix(slug);

  if (p === "/" || p === "") return prefix;

  if (p === prefix || p === `${prefix}/`) return prefix;

  if (p.startsWith(`${prefix}/`)) {
    const rest = p.slice(prefix.length) || "/";
    // Antes de PASS_THROUGH=/app, el input prefijaba `/app` → `/market/:slug/app/...`.
    // Esa forma interna ya no es canónica: si queda, output la achica a `/app/...` y
    // Transitioner ve publicHref distinto en cada rebuild → commitLocation infinito.
    if (PASS_THROUGH.test(rest)) return rest;
    return p;
  }

  if (p.startsWith("/market/")) return p;

  if (PASS_THROUGH.test(p)) return p;

  return `${prefix}${p.startsWith("/") ? p : `/${p}`}`;
}

/** Ruta interna → barra pública en host tenant. */
export function toPublicPathname(pathname: string, slug: string): string {
  const p = pathname || "/";
  if (PASS_THROUGH.test(p)) return p;

  const prefix = marketPrefix(slug);
  if (p === prefix || p === `${prefix}/`) return "/";
  if (p.startsWith(`${prefix}/`)) {
    const rest = p.slice(prefix.length) || "/";
    return rest;
  }
  return p;
}

function toInternal(url: URL, slug: string): URL {
  return withPathname(url, toInternalPathname(url.pathname, slug));
}

function toPublic(url: URL, slug: string): URL {
  return withPathname(url, toPublicPathname(url.pathname, slug));
}

export function tenantHostLocationRewrite(): LocationRewrite {
  return {
    input: ({ url }) => {
      const slug = peekTenantHostSlug(url.hostname);
      if (!slug) return url;
      return toInternal(url, slug);
    },
    output: ({ url }) => {
      const slug = peekTenantHostSlug(url.hostname);
      if (!slug) return url;
      return toPublic(url, slug);
    },
  };
}
